<template>
    <section class="flex flex-col items-stretch mb-4 px-4 md:px-8">
        <div class="flex flex-wrap justify-center gap-4 w-full">
            <div class="grid items-center min-w-[220px] flex-1 md:flex-none">
                <label class="text-left" for="from-date">Desde</label>
                <Calendar id="from-date" v-model="fromDate" showIcon :showOnFocus="false" class="w-full" dateFormat="dd/mm/yy" :disabled="formDisabled"/>
            </div>
            <div class="grid items-center min-w-[220px] flex-1 md:flex-none">
                <label class="text-left" for="to-date">Hasta</label>
                <Calendar id="to-date" v-model="toDate" showIcon :showOnFocus="false" class="w-full" dateFormat="dd/mm/yy" :disabled="formDisabled"/>
            </div>
            <div class="grid items-center min-w-[220px] flex-1 md:flex-none">
                <label class="text-left" for="company">Empresa</label>
                <Dropdown id="company" v-model="selectedCompany" :options="companies" optionLabel="name" placeholder="Selecciona una empresa"
                class="w-full" @change="companyChange" :disabled="formDisabled"/>
            </div>
            <div class="grid items-center min-w-[220px] flex-1 md:flex-none">
                <label class="text-left" for="branch">Sucursal</label>
                <Dropdown id="branch" v-model="selectedBranch" :options="selectedCompany.branches" optionLabel="name"
                placeholder="Selecciona una sucursal" class="w-full" :disabled="formDisabled" filter/>
            </div>
            <div class="grid items-center min-w-[220px] flex-1 md:flex-none">
                <label class="opacity-0" for="show-btn">Acción</label>
                <Button id="show-btn" label="Mostrar" @click="fillTable" :disabled="buttonDisabled"></Button>
            </div>
        </div>
    </section>
    <div class="mt-4 mb-4">
        <p><i class="pi pi-exclamation-triangle text-yellow-300"></i> El seleccionar una fecha muy amplia puede crear inestabilidad en el sistema, se recomienda precaución.</p>
    </div>
    <section class="grid justify-center px-4 md:px-8 text-zinc-900 dark:text-zinc-100" v-show="payments.length > 0">
        <div class="flex flex-wrap justify-between items-center gap-4 mb-4">
            <div>
                <div>
                    <h2 class="text-left">Análisis de pagos</h2>
                </div>
                <div>
                    <h3 class="text-left">{{ subtitle }}</h3>
                </div>
            </div>
            <div class="bg-zinc-200 text-zinc-900 dark:bg-zinc-800 dark:text-zinc-100 rounded-lg px-4 py-2 flex flex-wrap items-end shadow mr-2" v-show="payments.length > 0">
                <span class="text-sm mb-2 font-semibold self-center px-2">Ordenar por:</span>
                <div class="flex items-center gap-2">
                    <Button icon="pi pi-money-bill" text rounded value="amount" class="order-table-btn"
                    @click="sortTables('amount')" v-tooltip.bottom="'Cantidad'"/>
                    <Button icon="pi pi-sort-alpha-down" text rounded value="name" class="order-table-btn"
                    @click="sortTables('name')" v-tooltip.bottom="'Razón social'"/>
                    <Button icon="pi pi-calendar" text rounded value="date" class="order-table-btn"
                    @click="sortTables('date')" v-tooltip.bottom="'Fecha'" />
                </div>
            </div>
        </div>
        <div class="flex gap-2 mb-2">
            <Button 
                @click="exportXLSX" 
                class="btn-xlsx export-btn" 
                v-tooltip.top="'Exportar XLSX'"
            >
                <img src="../assets/svg/file_xlsx.svg" alt="XLSX" class="w-5 h-5" />
            </Button>
        </div>
        <div class="card overflow-auto text-zinc-900 dark:text-zinc-100">
            <DataTable
                :value="payments"
                stripedRows
                showGridlines
                tableStyle="min-width: 30rem"
                class="min-w-full payments-table"
                scrollable
                scrollHeight="625px">
                <Column field="serial" header="Folio"></Column>
                <Column field="date" header="Fecha" :bodyStyle="{ minWidth: '120px' }" :headerStyle="{ minWidth: '120px' }"></Column>
                <Column field="name" header="Razón Social"></Column>
                <Column field="bank" header="Banco del pago">
                    <template #body="{data}">
                        <div v-if="data.bank" class="flex items-center gap-2 justify-center">
                            <img :src="data.bank.image" :alt="data.bank.name" class="w-6 h-6"/>
                            <span>{{ data.bank.name }}</span>
                        </div>
                    </template>
                </Column>
                <Column field="amount" header="Cantidad"></Column>
            </DataTable>
        </div>
    </section>
</template>

<script>
import * as XLSX from "xlsx";
    import axiosInstance from '../axiosInstance';
    export default {
        data() {
            return {
                fromDate: new Date(),
                toDate: new Date(),
                selectedCompany: {},
                companies: [],
                selectedBranch: {},
                payments: [],
                buttonDisabled: false,
                formDisabled: false,
                subtitle: "",
                exportFields: {
                    serial: "Folio",
                    date: "Fecha",
                    name: "Razón Social",
                    bank: "Banco del pago",
                    amount: "Cantidad"
                },
                dateRange: "",
            }
        },

        methods: {
            /**
             * Fetches the initial data required for the payments analysis page.
             * This includes the default date range, companies, and branches.
             */
            async fetchPaymentsInitialData() {
                this.$emit("update-loading", true);
                this.buttonDisabled = true;
                this.formDisabled = true;
                let data = {};
                try {
                    const response = axiosInstance.post("/api/payments_viewer", {
                        token: localStorage.getItem('token')
                    });

                    data = await response;
            
                } catch(error) {
                    console.error("fetchPaymentsInitialData: There was an error!", error);
                    this.$toast.add({ severity: 'error', summary: 'Error',
                    detail: 'Hubo un problema al consultar los datos.', life: 5000, group: 'topr' });
                } finally {
                    this.$emit("update-loading", false);
                }

                return data;
            },

            /**
             * Sets the initial data for the payments analysis page.
             * This includes setting the date range, default company, and branches.
             */
            setInitialData() {
                this.fetchPaymentsInitialData().then((response) => {
                    if(response.status === 200){
                        const { from: fromDate, to: toDate, defaultCmp, companies } = response.data;
                        this.fromDate = this.obtainDateType(fromDate);
                        this.toDate = this.obtainDateType(toDate);

                        this.companies = companies.map((company) => {
                            return {
                                name: company.data.name,
                                id: company.data.id,
                                branches: company.branches
                            }
                        });

                        this.selectedCompany = {
                            name: defaultCmp.data.name, 
                            id: defaultCmp.data.id, 
                            branches: defaultCmp.branches
                        }

                        this.selectedBranch = this.selectedCompany.branches[0];
                        this.buttonDisabled = false;
                        this.formDisabled = false;
                    } else {
                        console.warn("setInitialData: There was a problem with the request.", response);
                        this.$toast.add({ severity: 'warn', summary: 'Aviso',
                        detail: 'Hubo un problema con la solicitud. Vuelve a intentarlo.', life: 5000, group: 'topr' });
                    }
                })
            },

            /**
             * Fetches the payment data based on the selected date range, company, and branch.
             */
            async fetchPaymentsData() {
                this.$emit("update-loading", true);
                this.buttonDisabled = true;
                let data = {};
                try {
                    const response = axiosInstance.post("/api/payments_viewer_data", {
                        token: localStorage.getItem('token'),
                        from: this.formatDate(this.fromDate),
                        to: this.formatDate(this.toDate),
                        company_id: this.selectedCompany.id,
                        branch_id: this.selectedBranch.id
                    });

                    data = await response;
            
                } catch(error) {
                    console.error("fetchPaymentsData: There was an error!", error);
                    this.$toast.add({ severity: 'error', summary: 'Error',
                    detail: 'Hubo un problema al consultar los datos.', life: 5000, group: 'topr' });
                } finally {
                    this.$emit("update-loading", false);
                    this.buttonDisabled=false;
                }

                return data;
            },

            /**
             * Fills the payments table with data based on the selected filters (date range, company, branch).
             */
            fillTable(){
                this.fetchPaymentsData().then((response) => {
                    if(response.status == 200) {
                        const { payments, total, subtitle } = response.data;

                        this.payments = Object.values(payments).map(({paid_at, name, serial_str, amount, bank}) => {
                            return {
                                date: paid_at,
                                name,
                                serial: serial_str,
                                amount,
                                bank
                            }
                        });

                        const totalData = {
                            serial: "",
                            date: "",
                            name: "",
                            amount: total
                        }

                        this.payments.push(totalData);
                        this.subtitle = subtitle;
                        this.dateRange = `${this.formatDate(this.fromDate).replace(/-/g, '')}-${this.formatDate(this.toDate).replace(/-/g, '')}`;
                    } else {
                        console.warn("fillTable: There was a problem with the request.", response);
                        this.$toast.add({ severity: 'warn', summary: 'Aviso',
                        detail: 'Hubo un problema con la solicitud. Vuelve a intentarlo.', life: 5000, group: 'topr' });
                    }
                })
            },

            sortTables(criterion){
                this.sortBy = criterion;
                if (this.payments.length < 2) return;

                const paymentsWithoutTotals = this.payments.slice(0, -1);
                const totals = this.payments[this.payments.length - 1];

                let sorted = [...paymentsWithoutTotals];
                if (criterion === 'amount') {
                    sorted.sort((a, b) => {
                        const aNum = parseFloat((a.amount || '0').toString().replace(/,/g, ''));
                        const bNum = parseFloat((b.amount || '0').toString().replace(/,/g, ''));
                        return bNum - aNum;
                    });
                } else if (criterion === 'name') {
                    sorted.sort((a, b) => a.name.trim().localeCompare(b.name.trim()));
                } else if (criterion === 'date') {
                    sorted.sort((a, b) => {
                        const aDate = new Date(a.date);
                        const bDate = new Date(b.date);
                        return bDate - aDate;
                    });
                }

                this.payments = [...sorted, totals];
            },

            obtainDateType(date){
                const [dateY, dateM, dateD] = date.split("-");
                return new Date(Number(dateY), Number(dateM) - 1, Number(dateD))
            },

            formatDate(date){
                const month = ((date.getMonth()+1).toString()).padStart(2, "0");
                const day = ((date.getDate()).toString()).padStart(2, "0");
                return `${date.getFullYear()}-${month}-${day}`
            },

            companyChange(){
                this.selectedBranch = this.selectedCompany.branches[0];
            },

            exportXLSX() {
                if (!Array.isArray(this.payments) || this.payments.length === 0) return;

                const fields = Object.keys(this.exportFields || {});
                if (fields.length === 0) return;

                const data = this.payments.map((item = {}) => {
                    const row = {};
                    fields.forEach((key) => {
                        let value = "";

                        if (key === "bank") {
                            const bankVal = item?.bank;
                            value = (bankVal && typeof bankVal === "object")
                                ? (bankVal?.name ?? "")
                                : (bankVal ?? "");
                        } else {
                            value = item?.[key] ?? "";
                        }

                        row[this.exportFields[key]] = value;
                    });
                    return row;
                });

                const ws = XLSX.utils.json_to_sheet(data);
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, "Pagos");
                XLSX.writeFile(wb, `pagos_${this.dateRange}.xlsx`);
            },
        },
        mounted() {
            this.setInitialData();

            // Observar tooltips para aplicar estilos personalizados
            const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeType === 1 && node.classList && node.classList.contains('p-tooltip')) {
                            const tooltipText = node.querySelector('.p-tooltip-text');
                            const tooltipArrow = node.querySelector('.p-tooltip-arrow');
                            
                            if (tooltipText) {
                                const text = tooltipText.textContent;
                                let color = null;
                                
                                if (text === 'Exportar XLSX') color = '#16a34a';
                                
                                if (color) {
                                    tooltipText.style.backgroundColor = color;
                                    tooltipText.style.color = 'white';
                                    if (tooltipArrow) {
                                        tooltipArrow.style.borderTopColor = color;
                                    }
                                }
                            }
                        }
                    });
                });
            });
            
            observer.observe(document.body, { childList: true, subtree: true });
            
            // Guardar el observer para limpiarlo después
            this._tooltipObserver = observer;
        },

        beforeUnmount() {
            if (this._tooltipObserver) {
                this._tooltipObserver.disconnect();
            }
        }

    }
</script>

<style scoped>
::v-deep(.p-datatable) {
    border-radius: 0.5rem !important;
    overflow: hidden !important;
    box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1) !important;
}

::v-deep(.p-datatable tbody tr td) {
    text-align: right !important;
}

::v-deep(.payments-table.p-datatable .p-datatable-thead > tr > th) {
    background-color: #0f1338 !important;
    color: white !important;
}

::v-deep(.payments-table.p-datatable .p-datatable-tbody > tr:not(:last-child):nth-child(odd) > td) {
    background-color: #f3f4f6 !important;
    color: #111827 !important;
}

::v-deep(.payments-table.p-datatable .p-datatable-tbody > tr:not(:last-child):nth-child(even) > td) {
    background-color: #ffffff !important;
    color: #111827 !important;
}

::v-deep(.payments-table.p-datatable .p-datatable-tbody > tr:not(:last-child) > td:first-child) {
    text-align: left !important;
    /* background-color: #dcfce7 !important; */
    color: #166534 !important;
    font-weight: 600 !important;
}

::v-deep(.payments-table.p-datatable .p-datatable-tbody > tr:last-child > td) {
    background-color: #0f1338 !important;
    color: white !important;
    font-weight: 600 !important;
}

::v-deep(.payments-table.p-datatable .p-datatable-tbody > tr:last-child > td:first-child) {
    background-color: #0f1338 !important;
    color: white !important;
    font-weight: 600 !important;
    text-align: left !important;
}

::v-deep(.p-calendar .p-button) {
    @apply bg-zinc-200 text-zinc-900 border-zinc-300 hover:bg-zinc-300 dark:bg-zinc-700 dark:text-zinc-100 dark:border-zinc-600 dark:hover:bg-zinc-600 transition-colors;
}

::v-deep(.p-calendar .p-button:hover) {
    @apply bg-zinc-300 dark:bg-zinc-600;
}

#show-btn {
    @apply bg-zinc-200 text-zinc-900 border border-zinc-300 hover:bg-zinc-300 dark:bg-zinc-700 dark:text-zinc-100 dark:border-zinc-600 dark:hover:bg-zinc-600 rounded px-4 py-2 text-lg shadow focus:outline-none transition-colors;
}

Button.order-table-btn {
    @apply bg-zinc-200 text-zinc-900 border-zinc-300 hover:bg-zinc-300 dark:bg-zinc-700 dark:text-zinc-100 dark:border-zinc-600 dark:hover:bg-zinc-600 transition-colors;
}

/* Export buttons base styles */
::v-deep(.export-btn) {
    @apply rounded p-2 shadow transition-colors flex items-center justify-center;
    width: 2.5rem;
    height: 2.5rem;
}

::v-deep(.export-btn img) {
    filter: brightness(0) invert(1);
}

::v-deep(.btn-xlsx) {
    @apply bg-green-600 text-white;
    border: 1px solid #16a34a;
}

::v-deep(.btn-xlsx:hover) {
    @apply bg-green-700 text-white !important;
    border: 1px solid #166534 !important;
}
</style>

<style>
.dark .payments-table.p-datatable .p-datatable-thead > tr > th {
    background-color: #D9D9D9 !important;
    color: #0f1338 !important;
}

.dark .payments-table.p-datatable .p-datatable-tbody > tr > td {
    color: white !important;
}

.dark .payments-table.p-datatable .p-datatable-tbody > tr:not(:last-child):nth-child(odd) > td {
    background-color: #18181b !important;
    color: white !important;
}

.dark .payments-table.p-datatable .p-datatable-tbody > tr:not(:last-child):nth-child(even) > td {
    background-color: #27272a !important;
    color: white !important;
}

.dark .payments-table.p-datatable .p-datatable-tbody > tr:not(:last-child) > td:first-child {
    /* background-color: #166534 !important; */
    color: #9bffbe !important;
    font-weight: 600 !important;
}

.dark .payments-table.p-datatable .p-datatable-tbody > tr:last-child > td {
    background-color: #D9D9D9 !important;
    color: #0f1338 !important;
}

.dark .payments-table.p-datatable .p-datatable-tbody > tr:last-child > td:first-child {
    background-color: #D9D9D9 !important;
    color: #0f1338 !important;
}
</style>